import React from 'react';
import { connect } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import 'isomorphic-unfetch';
import mergeWith from 'lodash/mergeWith';
import { mapStateToProps, dispatchProps } from './utils';
import FeedbackButton from '../feedback/feedback-button';
import { readPersonalizationSettings } from '../user/storage/personalization';
import productPageQuery from './query.graphql';
import { PRODUCT_CONTEXT_ID } from './constants';
import { ProductPageQuery } from '../../types';
import { ProductPageProps } from './types';
import {
  Variables,
  RouteReducerState,
  RouteReducerActions,
  Loaded,
  Group,
} from './product-types';
import { OfferListFilterContextContainer } from './components/offer-list-filter-context';
import { defaultProductState } from './reducer';
import { ProductPage } from './product-page';

const Product = (props: ProductPageProps) => {
  const { data, me } = props;
  const { t } = useAppContext();

  if (!data.product) {
    return null;
  }

  return (
    <OfferListFilterContextContainer>
      {me && me.analyticsAccess && (
        <FeedbackButton>{t('feedback', 'button.text')}</FeedbackButton>
      )}
      <ProductPage {...props} />
    </OfferListFilterContextContainer>
  );
};

Product.graphQL = productPageQuery;

// Group on product ID, ignore section param
Product.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

Product.variables = ({ id, offset, section, marketCode }: Variables) => {
  const expertsEnabled = ['se'].includes(marketCode);
  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
    personalizationExcludeCategories: readPersonalizationSettings().notInterestedIn.categories.map(
      (category: { id: string }) => +category.id
    ),
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    advized: expertsEnabled,
    condition: 'new',
  };
};

Product.reducer = (state: RouteReducerState, action: RouteReducerActions) => {
  switch (action.type) {
    case 'PRODUCT_CONDITION':
      return {
        ...state,
        condition: action.condition,
      };
    default: {
      return state ?? defaultProductState;
    }
  }
};

Product.stateToVariables = (state: RouteReducerState) => {
  if (!state) {
    return {};
  }
  return {
    ...state,
    priceList: true,
    condition: state.condition ?? 'new',
  };
};

Product.loadOrder = (
  { section = 'main', marketCode = undefined } = {},
  loaded = {} as Loaded,
  options = { initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  return [
    {
      main: !loaded.main,
      ...sectionContent[section],
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

Product.mergeData = (oldData: ProductPageQuery, newData: ProductPageQuery) => {
  const data = mergeWith({}, oldData, newData);

  // If price data was refetched always use data from latest fetch
  if (newData?.product?.prices) {
    return {
      ...data,
      product: {
        ...data.product,
        prices: newData.product.prices,
      },
    };
  }

  return data;
};

export default connect(mapStateToProps, dispatchProps)(Product);
